import { defineStore } from "pinia"
import { useAccountStore } from "@/stores/account"
import { updateGa4UserId, ga4Event, ga4ScreenView } from "@/utils/gtag_utils"
import { putHubSpotContact, sendUserAgentToHubSpot } from "@/utils/hubspot_utils"
import type { RouteLocationNormalized } from "vue-router"
import { routeIdOf } from "@/utils/resource_ids"
import { useLoginStore } from "@/stores/login"
import { computed } from "vue"

export const useTrackingsStore = defineStore('trackings', () => {
  const updateTrackingUser = async (accountId: string) => {
    const accountStore = useAccountStore()
    const accountInfo = await accountStore.getAccountInfo({ request_current_disk_byte: false });
    if (!accountInfo)  return

    updateGa4UserId(accountId)

    putHubSpotContact({ email: accountInfo.mail }, "")
    sendUserAgentToHubSpot()
  }

  const trackRouteView = (route: RouteLocationNormalized) => {
    if(!route.name) return;
    const routeName = route.name.toString()
    const routeId = routeIdOf(routeName);

    const eventParams: Record<string, string> = {}
    if ("cloudConstructionId" in route.params) {
      eventParams["工事ID"] = route.params.cloudConstructionId as string
    }
    if ("construction_id" in route.query) {
      eventParams["工事ID"] = route.query.construction_id as string
    }

    if ("photoKey" in route.params) {
      eventParams["写真キー"] = route.params.photoKey as string
    }
    if ("photo_key" in route.query) {
      eventParams["写真キー"] = route.query.photo_key as string
    }

    if ("kokubanHash" in route.params) {
      eventParams["黒板ハッシュ"] = route.params.kokubanHash as string
    }
    if ("kokubah_hash" in route.query) {
      eventParams["黒板ハッシュ"] = route.query.kokubah_hash as string
    }

    ga4ScreenView(route.fullPath)

    ga4Event(
      `表示_${routeName}`,
      {
        screen_name: `${routeId}-${routeName}`,
        event_action: '画面遷移',
        value: routeId,
        routeId,
        routeName,
        ...eventParams,
      }
    )
  }

  const loginStore = useLoginStore()
  const accountId = computed(() => {
    return loginStore.currentAccount?.account_id;
  })

  const trackDaichoViewEvent = (eventId: DaichoViewEventId, constructionId: string)  => {
    const eventName = getDaichoViewEventNameFromEventId(eventId)
    console.info('daichoViewEvent:', eventName, `(${eventId})`, )
    ga4Event(eventName, {
      "工事ID": constructionId,
      constructionId,
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    })
  }

  const trackKuradenEvent = (eventId: KuradenEventId, constructionId?: string) => {
    const eventName = getKuradenEventNameFromEventId(eventId)
    const eventParams: Record<string, any> = {
      eventId,
      account_id: accountId.value,
      transport_type: 'beacon',
    }

    if (constructionId) {
      eventParams["工事ID"] = constructionId
      eventParams.constructionId = constructionId
    }

    ga4Event(eventName, eventParams)
  }

  return {
    updateTrackingUser,
    trackRouteView,
    trackDaichoViewEvent,
    trackKuradenEvent,
  }
})

// 台帳ビューのイベント
const DAICHO_VIEW_TRACKING_EVENTS = {
  constructionListPhoto: "台帳ビュー_工事一覧_写真",
  treeViewDialogFreePlanShowPremium: "台帳ビュー_ダイアログ_フリー向け_プレミアムを詳しく見る",
  treeViewDialogFreePlanClose: "台帳ビュー_ダイアログ_フリー向け_閉じる",
  treeViewDialogGeneralPermissionOk: "台帳ビュー_ダイアログ_一般向け_OK",
  treeViewDialogExistingConstructionOk: "台帳ビュー_ダイアログ_既存工事_OK",
  treeViewDialogCalsOk: "台帳ビュー_ダイアログ_電子納品_OK",
  shareSelect: "台帳ビュー_共有_選択",
  shareOtherMember: "台帳ビュー_共有_上記の人以外に共有する",
  shareInputMessage: "台帳ビュー_共有_メッセージ入力欄",
  shareSend: "台帳ビュー_共有_送信",
  shareClose: "台帳ビュー_共有_閉じる",
  shareOtherMemberMailAddress: "台帳ビュー_共有_共有相手のメールアドレス",
  shareOtherMemberName: "台帳ビュー_共有_あなたの名前",
  shareOtherMemberMessage: "台帳ビュー_共有_メッセージ",
  shareOtherMemberBack: "台帳ビュー_共有_戻る",
} as const;
type DaichoViewEventId = keyof typeof DAICHO_VIEW_TRACKING_EVENTS;
const getDaichoViewEventNameFromEventId = (eventId: DaichoViewEventId) => (DAICHO_VIEW_TRACKING_EVENTS[eventId])

// 蔵電のイベント
const KURADEN_TRACKING_EVENTS = {
  constructionListHeaderPhoneWhoToCallGroupModeOff: "工事一覧_ヘッダー電話_だれに話しますか？_グループモード解除",
  constructionInnerHeaderPhoneWhoToCallGroupModeOff: "工事内_ヘッダー電話_だれに話しますか？_グループモード解除",
  photoDetailKuradenIcon: "写真詳細__蔵電アイコン",
  photoDetailWhoToCallClose: "写真詳細__だれに電話しますか？_とじる",
  photoDetailWhoToCallStart: "写真詳細__だれに電話しますか？_通話を開始",
  photoDetailWhoToCallGroupMode: "写真詳細__だれに電話しますか？_グループモード",
  photoDetailWhoToCallGroupModeOff: "写真詳細__だれに電話しますか？_グループモード解除",
  zumenDetailKuradenIcon: "図面詳細__蔵電アイコン",
  zumenDetailWhoToCallClose: "図面詳細__だれに電話しますか？_とじる",
  zumenDetailWhoToCallStart: "図面詳細__だれに電話しますか？_通話を開始",
  zumenDetailWhoToCallGroupMode: "図面詳細__だれに電話しますか？_グループモード",
  zumenDetailWhoToCallGroupModeOff: "図面詳細__だれに電話しますか？_グループモード解除",
  kokubanDetailKuradenIcon: "黒板詳細__蔵電アイコン",
  kokubanDetailWhoToCallClose: "黒板詳細__だれに電話しますか？_とじる",
  kokubanDetailWhoToCallStart: "黒板詳細__だれに電話しますか？_通話を開始",
  kokubanDetailWhoToCallGroupMode: "黒板詳細__だれに電話しますか？_グループモード",
  kokubanDetailWhoToCallGroupModeOff: "黒板詳細__だれに電話しますか？_グループモード解除",
  incomingCallDialogReject: "受電ダイアログ__拒否",
  incomingCallDialogAnswer: "受電ダイアログ__応答",
  operationLockToast: "蔵クラWeb_画面共有_クラ電中は操作できません",
} as const;
export type KuradenEventId = keyof typeof KURADEN_TRACKING_EVENTS;
const getKuradenEventNameFromEventId = (eventId: KuradenEventId) => (KURADEN_TRACKING_EVENTS[eventId])

